import { useEffect, useState } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, LayoutGrid, X } from 'lucide-react';
import { Modal, Skeleton } from '@/components/ui';
import type { Category } from '@/types';
import { useCategoryChildren, useTopCategories } from '@/hooks/useCategories';
import { iconForCategory } from '@/utils/categoryIcons';

export interface CategoryPickerProps {
  open: boolean;
  onClose: () => void;
  /** null = "Show all categories". */
  onSelect: (category: Category | null) => void;
  /** Pre-drill into this category when the picker opens. */
  initial?: Pick<Category, 'id' | 'title' | 'path'> | null;
}

function RowSkeletons() {
  return (
    <>
      {Array.from({ length: 6 }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-line-light px-4 py-3.5 last:border-0"
        >
          <Skeleton className="h-9 w-9 rounded-lg" />
          <Skeleton className="h-4 w-40 rounded" />
        </div>
      ))}
    </>
  );
}

export function CategoryPicker({ open, onClose, onSelect, initial }: CategoryPickerProps) {
  // Drill-down stack of category levels; empty = root.
  const [stack, setStack] = useState<Category[]>([]);

  useEffect(() => {
    if (open) setStack(initial ? [initial] : []);
  }, [open, initial]);

  const current = stack.length > 0 ? stack[stack.length - 1] : null;
  const top = useTopCategories();
  const children = useCategoryChildren(current ? current.id : null);
  const { items, loading } = current ? children : top;

  const breadcrumb = ['All categories', ...stack.slice(0, -1).map((c) => c.title)].join(' / ');

  const handleRowClick = (category: Category) => {
    const hasChildren = (category.subCategories?.length ?? 0) > 0;
    if (hasChildren) {
      setStack((prev) => [...prev, category]);
    } else {
      onSelect(category);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="lg" ariaLabel="Choose category">
      {/* Header: back + breadcrumb/title + close */}
      <div className="flex items-center gap-1 border-b border-line px-2 py-2">
        {current && (
          <button
            type="button"
            onClick={() => setStack((prev) => prev.slice(0, -1))}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-secondary transition-all duration-200 hover:bg-line-light hover:text-ink"
            aria-label="Back"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        <div className="min-w-0 flex-1 px-2">
          {current && <p className="truncate text-xs text-ink-tertiary">{breadcrumb}</p>}
          <p className="truncate text-base font-bold text-ink">
            {current ? current.title : 'All categories'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-secondary transition-all duration-200 hover:bg-line-light hover:text-ink"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Apply row */}
      {current ? (
        <button
          type="button"
          onClick={() => onSelect(current)}
          className="flex w-full items-center gap-3 bg-primary/10 px-4 py-3.5 text-left transition-colors duration-200 hover:bg-primary/15"
        >
          <CheckCircle2 size={20} className="shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate text-sm font-bold text-primary">
            Apply &ldquo;{current.title}&rdquo;
          </span>
          <span className="shrink-0 text-xs font-medium text-primary/70">
            includes everything inside
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors duration-200 hover:bg-line-light"
        >
          <LayoutGrid size={20} className="shrink-0 text-ink-secondary" />
          <span className="text-sm font-bold text-ink-secondary">Show all categories</span>
        </button>
      )}

      {/* Children list */}
      <div className="max-h-[60vh] min-h-[280px] overflow-y-auto border-t border-line-light pb-4">
        {loading && items.length === 0 ? (
          <RowSkeletons />
        ) : (
          items.map((category) => {
            const Icon = iconForCategory(category.title);
            const hasChildren = (category.subCategories?.length ?? 0) > 0;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => handleRowClick(category)}
                className="flex w-full items-center gap-3 border-b border-line-light px-4 py-3.5 text-left transition-colors duration-200 last:border-0 hover:bg-line-light"
              >
                <span className="shrink-0 rounded-lg bg-primary/15 p-2">
                  <Icon size={18} className="text-primary" />
                </span>
                <span className="min-w-0 flex-1 truncate text-base font-medium text-ink">
                  {category.title}
                </span>
                {hasChildren ? (
                  <ChevronRight size={18} className="shrink-0 text-ink-tertiary" />
                ) : (
                  <CheckCircle2 size={18} className="shrink-0 text-ink-tertiary" />
                )}
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
}

export default CategoryPicker;
